import { useEffect, useMemo, useState } from "react";

import { ConfirmModal } from "@common";

// "2026-08-15" → "Sat, Aug 15". Parsed field-by-field, never through Date(iso),
// which reads the string as UTC and can land the label a day off.
function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return "—";
  return new Date(y, m - 1, d).toLocaleDateString("en-CA", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function fmtMoney(value) {
  if (value == null || value === "") return "—";
  return `$${Number(value).toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// Passed where a handler is required but must do nothing; a new arrow here would
// give the modal a fresh prop identity on every render.
const NOOP = () => {};

function Chevron({ open }) {
  return (
    <svg
      viewBox="0 0 10 6"
      aria-hidden="true"
      className={`h-2.5 w-2.5 shrink-0 fill-none stroke-current stroke-[1.5] transition-transform ${
        open ? "" : "-rotate-90"
      }`}
    >
      <path d="M1 1l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const CONFIDENCE = {
  high: { label: "Exact", cls: "bg-palette-emerald-soft text-palette-emerald-text" },
  medium: { label: "Close", cls: "bg-palette-sky-soft text-palette-sky-text" },
  low: { label: "Check", cls: "bg-palette-amber-soft text-palette-amber-text" },
};

/**
 * One side of the comparison — the schedule, NetSuite's ledger, or the bank.
 *
 * All three render through the same component on purpose: the reader's job is to
 * spot where the three DISAGREE, and that only works if they are laid out
 * identically. A missing side shows as "nothing here" rather than an empty cell,
 * because "the bank has no row for this" is itself the finding.
 */
/**
 * The bank side when ONE charge arrived as several bank lines and the matcher
 * added them back up — the card centre bills a month's fees as separate
 * MON/VSA/MC/INT/AMX rows against a single "bank charges" schedule.
 *
 * The total leads, because that is the figure the match was made on; the lines
 * are listed under it because a person confirming the match has to see that
 * they really are the same charge and really do add up. `Side` cannot be reused
 * here: it renders one amount, and given the null `bank_feed` a combined match
 * carries it would print "nothing here" over a payment reported as cleared.
 */
function CombinedSide({ lines }) {
  const total = lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className="font-semibold tabular-nums text-palette-gray-primary">
          {fmtMoney(total)}
        </span>
        <span className="rounded bg-palette-gray-subtle px-1 text-[9px] font-semibold uppercase tracking-wide text-palette-gray-secondary">
          {lines.length} bank lines
        </span>
      </div>
      <div className="text-[11px] text-palette-gray-secondary">{fmtDate(lines[0]?.date)}</div>
      <div className="mt-1 space-y-0.5 border-l border-palette-gray-divider pl-2">
        {lines.map((l) => (
          <div
            key={l.reference}
            className="flex items-baseline justify-between gap-2 text-[11px] leading-tight text-palette-gray-secondary"
          >
            <span className="break-words">{l.memo}</span>
            <span className="shrink-0 tabular-nums">{fmtMoney(l.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Side({ amount, date, category, memo, reference, link, muted }) {
  if (amount == null && !date && !memo) {
    return <div className="text-xs italic text-palette-gray-placeholder">nothing here</div>;
  }
  return (
    <div className={muted ? "opacity-70" : undefined}>
      <div className="flex items-baseline gap-2">
        <span className="font-semibold tabular-nums text-palette-gray-primary">
          {fmtMoney(amount)}
        </span>
        {/* The link sits beside the figure being judged, so checking a match in
            NetSuite is one click from it. stopPropagation because the row is
            clickable — opening NetSuite must not also tick the box. */}
        {link && (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={`Open ${reference ?? "this transaction"} in NetSuite`}
            className="text-[10px] font-semibold text-palette-indigo-heading underline decoration-dotted underline-offset-2 hover:decoration-solid"
          >
            open
          </a>
        )}
      </div>
      <div className="text-[11px] text-palette-gray-secondary">{fmtDate(date)}</div>
      {category && (
        <div className="mt-0.5 inline-block rounded bg-palette-gray-subtle px-1 text-[9px] font-semibold uppercase tracking-wide text-palette-gray-secondary">
          {category}
        </div>
      )}
      {/* The memo is what a person actually reads to judge a match, so it is
          never truncated with an ellipsis — it wraps. */}
      {memo && (
        <div className="mt-0.5 break-words text-[11px] leading-tight text-palette-gray-secondary">
          {memo}
        </div>
      )}
      {/* Printed even when there is no link: a bank-feed row has no record page
          in NetSuite, but its id is still how it is found in the feed. */}
      {reference && (
        <div className="mt-0.5 font-mono text-[9px] text-palette-gray-placeholder">{reference}</div>
      )}
    </div>
  );
}

/**
 * Confirm which scheduled payments have actually cleared the bank.
 *
 * Three columns — SCHEDULE, NETSUITE, BANK — because the reconciler's match is a
 * heuristic and the only way to judge one is to see the three sides together.
 * The amounts often disagree legitimately: a term loan debits principal and
 * interest in one line, so the bank figure is larger than the scheduled payment
 * and the MEMO is what pairs them. A row that hid that disagreement would be
 * asking for agreement to something unexamined.
 *
 * Confirming WRITES — it records a human decision against the occurrence
 * (SchedulePaymentClearance), which then overrides the heuristic permanently.
 * That is why nothing is confirmed without a click, and why low-confidence rows
 * start unticked: a guess that shares a modal with nine good ones must not be
 * carried in on their coat-tails.
 *
 * CONFIRMATIONS ONLY. There is no "did not clear" — leaving a row unticked
 * decides nothing, so the occurrence stays on the automatic reading and comes
 * back next time. That is the right home for anything the reader is unsure
 * about, and it means a stored row always says the same thing: a person saw
 * this and agreed it cleared.
 */
export default function ConfirmClearedModal({
  isOpen,
  items,
  onClose,
  onConfirm,
  isSaving,
  // Open the Edit-payment modal for one occurrence. A row here is a payment
  // that is DUE and unsettled, so "the amount is wrong" and "this one never
  // went out" are both live answers — and neither is a clearance decision, so
  // neither belongs on the tickbox. Omitted by a host that cannot edit
  // payments, and the row stops being clickable with it.
  onEditOccurrence,
}) {
  // Keyed by `schedule:original_date`, the same occurrence identity the API
  // writes against — never by array index, which reorders as data refreshes.
  const [ticked, setTicked] = useState({});
  // Keyed by account id, like `ticked` is by occurrence — never by index, which
  // reorders as the underlying data refreshes.
  const [collapsed, setCollapsed] = useState({});

  const rows = useMemo(
    () =>
      (items ?? []).map((item) => ({
        ...item,
        key: `${item.schedule}:${item.original_date}`,
      })),
    [items],
  );

  // Grouped by ACCOUNT, not clinic: a clinic can hold more than one account and
  // an entity-level account belongs to no clinic, so the account is the only
  // grouping that is always well defined. The clinic code rides along in the
  // heading because that is what people call these by.
  //
  // Reconciliation is done account by account — you check one bank's statement
  // at a time — so a flat list interleaving three accounts asks the reader to
  // re-orient on every row.
  const groups = useMemo(() => {
    const byAccount = new Map();
    rows.forEach((row) => {
      const id = row.bank_account ?? "none";
      if (!byAccount.has(id)) {
        byAccount.set(id, {
          id,
          clinic: row.clinic,
          label: row.bank_account_label || `Account ${id}`,
          rows: [],
        });
      }
      byAccount.get(id).rows.push(row);
    });
    return [...byAccount.values()].sort(
      (a, b) =>
        String(a.clinic ?? "\uffff").localeCompare(String(b.clinic ?? "\uffff")) ||
        String(a.label).localeCompare(String(b.label)),
    );
  }, [rows]);

  // Re-seeded whenever the modal opens on a new set: high confidence pre-ticked,
  // anything weaker left for a person to look at first.
  useEffect(() => {
    if (!isOpen) return;
    const seed = {};
    rows.forEach((r) => {
      seed[r.key] = r.match?.confidence === "high";
    });
    setTicked(seed);
    // Everything opens expanded. Collapsing is for putting an account aside
    // once you have dealt with it, so starting collapsed would hide the work
    // rather than organise it.
    setCollapsed({});
  }, [isOpen, rows]);

  const selected = rows.filter((r) => ticked[r.key]);
  const allTicked = rows.length > 0 && selected.length === rows.length;

  const toggle = (key) => setTicked((prev) => ({ ...prev, [key]: !prev[key] }));
  const toggleAll = () => {
    const next = {};
    rows.forEach((r) => {
      next[r.key] = !allTicked;
    });
    setTicked(next);
  };
  // Per-account tick-all. The realistic workflow is "this account's statement
  // agrees, all of it" — doing that row by row across forty payments is where
  // people give up, and the global tick-all is too blunt when only one
  // account has been checked.
  // Counted off the GROUPS, not off the collapsed map: that map can hold ids
  // from a previous set of items, and a stale key would make "Expand all" look
  // enabled with nothing to expand.
  const collapsedCount = groups.filter((g) => collapsed[g.id]).length;

  const toggleCollapsed = (id) => setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  const setAllCollapsed = (value) =>
    setCollapsed(value ? Object.fromEntries(groups.map((g) => [g.id, true])) : {});

  const toggleGroup = (group) => {
    const groupAllTicked = group.rows.every((r) => ticked[r.key]);
    setTicked((prev) => {
      const next = { ...prev };
      group.rows.forEach((r) => {
        next[r.key] = !groupAllTicked;
      });
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(
      selected.map((r) => ({
        schedule: r.schedule,
        original_date: r.original_date,
        cleared: true,
        // The BANK's date where there is one — a cleared date should say when
        // the money actually MOVED, not when it posted. Falling back to the
        // match date covers a cleared ledger line with no feed row; falling
        // back to the occurrence's own date covers a payment with no evidence
        // at all, where its due date is the only date anyone has.
        cleared_date: r.match?.bank_feed?.date || r.match?.date || r.date || null,
        amount: r.amount,
        // The two are recorded differently on purpose. `confirmed_match` means
        // a person agreed with evidence the system found; `manual` means they
        // asserted it with none. Reading those back as the same thing would
        // lose the distinction that matters most when auditing later.
        source: r.match ? "confirmed_match" : "manual",
        matched_reference: r.match?.reference || "",
        cleared_note: r.match
          ? `Confirmed ${r.match.source} match ${r.match.reference} ` +
            `($${r.match.amount} on ${r.match.date}, matched by ${r.match.matched_by})`
          : "Marked cleared by hand — no NetSuite or bank row was found for it.",
      })),
    );
  };

  const TH = "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider";

  return (
    <ConfirmModal
      isOpen={isOpen}
      // Escape and the backdrop are inert mid-save: dismissing while the write
      // is in flight would misreport it as declined.
      onClose={isSaving ? NOOP : onClose}
      onConfirm={handleConfirm}
      isLoading={isSaving}
      confirmDisabled={selected.length === 0}
      // Three columns of amount + date + category + memo need the room; at a
      // narrower width the memos wrap into unreadable columns and the whole
      // point of the side-by-side is lost.
      size="3xl"
      title="Confirm cleared payments"
      confirmVariant="primary"
      confirmText={selected.length === 1 ? "Mark 1 cleared" : `Mark ${selected.length} cleared`}
      cancelText="Not now"
      message={
        <div>
          <p className="text-sm text-palette-gray-secondary">
            Every scheduled payment that was due and has not been settled yet. The three columns are
            the same payment as the SCHEDULE predicts it, as NETSUITE booked it, and as the BANK
            reports it — matching pairs them on amount, date and memo, and the amounts can
            legitimately differ (a loan payment includes interest). Rows marked{" "}
            <span className="font-semibold text-palette-rose-text">No match</span> have nothing on
            either side: the money may have gone out without being booked, so those are a judgement
            call rather than an agreement. Tick whatever you know has cleared.
          </p>
          <p className="mt-1 text-sm text-palette-gray-secondary">
            Only confirmations are recorded. Leaving a row unticked decides nothing — it stays as
            the automatic reading and will be offered again next time, which is what you want for
            anything you are unsure about.
          </p>
          {/* Said in the copy AND shown per row: the tick and the row click do
              two different things to the same payment, and a reader who assumes
              the row is inert will never find the second one. */}
          {onEditOccurrence && (
            <p className="mt-1 text-sm text-palette-gray-secondary">
              Something wrong with a payment itself?{" "}
              <span className="font-semibold text-palette-gray-heading">Click the row</span> to edit
              its date or amount, or to skip it — that is a change to the payment, not a decision
              about whether it cleared.
            </p>
          )}

          {/* Worth its place only once there is more than one account to move
              between — with a single group the two buttons would do nothing a
              reader could not see. */}
          {groups.length > 1 && (
            <div className="mt-3 flex items-center gap-2 text-[11px]">
              <span className="text-palette-gray-secondary">
                {groups.length} accounts · {collapsedCount} collapsed
              </span>
              <button
                type="button"
                onClick={() => setAllCollapsed(false)}
                disabled={collapsedCount === 0}
                className="rounded-lg border border-palette-gray-divider px-2 py-1 font-semibold text-palette-gray-secondary transition-colors hover:bg-palette-gray-subtle disabled:opacity-40"
              >
                Expand all
              </button>
              <button
                type="button"
                onClick={() => setAllCollapsed(true)}
                disabled={collapsedCount === groups.length}
                className="rounded-lg border border-palette-gray-divider px-2 py-1 font-semibold text-palette-gray-secondary transition-colors hover:bg-palette-gray-subtle disabled:opacity-40"
              >
                Collapse all
              </button>
            </div>
          )}

          <div className="mt-4 overflow-x-auto rounded-xl border border-palette-gray-divider">
            <table className="w-full min-w-[52rem] text-sm">
              <thead>
                <tr className="bg-palette-gray-subtle text-palette-gray-secondary">
                  <th className={`${TH} w-10`}>
                    <input
                      type="checkbox"
                      checked={allTicked}
                      onChange={toggleAll}
                      title={allTicked ? "Untick all" : "Tick all"}
                      className="h-4 w-4 cursor-pointer rounded border-palette-gray-divider"
                    />
                  </th>
                  <th className={TH}>Scheduled payment</th>
                  <th className={TH}>NetSuite transaction</th>
                  <th className={TH}>Bank transaction</th>
                  <th className={`${TH} w-28`}>Match</th>
                  {onEditOccurrence && <th className={`${TH} w-20`}>Payment</th>}
                </tr>
              </thead>
              {groups.map((group) => {
                const groupRows = group.rows;
                const groupTicked = groupRows.filter((r) => ticked[r.key]).length;
                const groupTotal = groupRows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
                const groupMatched = groupRows.filter((r) => r.match).length;
                const isCollapsed = !!collapsed[group.id];
                return (
                  <tbody
                    key={group.id}
                    className="divide-y divide-palette-gray-divider border-t-2 border-palette-gray-divider"
                  >
                    <tr className="bg-palette-gray-subtle">
                      <td className="px-3 py-2 align-middle">
                        <input
                          type="checkbox"
                          checked={groupTicked === groupRows.length}
                          // Partly ticked reads as neither on nor off, which is
                          // what it is — without this the box shows empty and
                          // invites a click that would UNtick the ones already
                          // chosen in this account.
                          ref={(el) => {
                            if (el)
                              el.indeterminate = groupTicked > 0 && groupTicked < groupRows.length;
                          }}
                          onChange={() => toggleGroup(group)}
                          title={`Tick every payment on ${group.label}`}
                          className="h-4 w-4 cursor-pointer rounded border-palette-gray-divider"
                        />
                      </td>
                      <td colSpan={onEditOccurrence ? 5 : 4} className="px-3 py-2">
                        {/* The whole heading is the control, not a lone
                            chevron: a 10px target is a miss waiting to happen,
                            and there is nothing else to click here. The
                            checkbox sits in its own cell so ticking a whole
                            account never toggles the disclosure by accident. */}
                        <button
                          type="button"
                          onClick={() => toggleCollapsed(group.id)}
                          aria-expanded={!isCollapsed}
                          title={isCollapsed ? "Show these payments" : "Hide these payments"}
                          className="flex w-full flex-wrap items-baseline gap-x-2 gap-y-0.5 text-left"
                        >
                          <Chevron open={!isCollapsed} />
                          {group.clinic && (
                            <span className="text-xs font-bold uppercase tracking-wide text-palette-gray-heading">
                              {group.clinic}
                            </span>
                          )}
                          <span className="text-xs font-semibold text-palette-gray-heading">
                            {group.label}
                          </span>
                          <span className="text-[11px] text-palette-gray-secondary">
                            {groupRows.length} due · {groupMatched} matched · {fmtMoney(groupTotal)}
                          </span>
                          {/* Shown COLLAPSED too — the count is the reason a
                              reader can safely put an account away. */}
                          {groupTicked > 0 && (
                            <span className="text-[11px] font-semibold text-palette-emerald-text">
                              {groupTicked} ticked
                            </span>
                          )}
                        </button>
                      </td>
                    </tr>
                    {!isCollapsed &&
                      groupRows.map((row) => {
                        const m = row.match ?? {};
                        const conf = CONFIDENCE[m.confidence] ?? CONFIDENCE.low;
                        const isTicked = !!ticked[row.key];
                        const editable = !!onEditOccurrence;
                        return (
                          <tr
                            key={row.key}
                            onClick={editable ? () => onEditOccurrence(row) : undefined}
                            title={
                              editable
                                ? "Click to edit this payment's date or amount, or skip it"
                                : undefined
                            }
                            // Green wash marks a row as SELECTED, not merely as
                            // matched: every row here matched something, so colouring
                            // by match would say nothing. It tracks the tickbox so
                            // what is about to be written is visible at a glance.
                            className={`${isTicked ? "bg-palette-emerald-soft/40" : "bg-white"} ${
                              editable ? "cursor-pointer hover:bg-palette-indigo-soft" : ""
                            }`}
                          >
                            {/* The checkbox CELL swallows the click: ticking a
                                row records that it cleared, opening the editor
                                says it needs changing, and one gesture must not
                                do both. */}
                            <td
                              className="px-3 py-2 align-top"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={isTicked}
                                onChange={() => toggle(row.key)}
                                className="h-4 w-4 cursor-pointer rounded border-palette-gray-divider"
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <Side
                                amount={row.amount}
                                date={row.date}
                                category={row.expense_type}
                                memo={
                                  row.rolled_forward_from
                                    ? `carried onto ${fmtDate(row.display_date)}`
                                    : null
                                }
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <Side
                                amount={m.ledger?.amount}
                                date={m.ledger?.date}
                                category={m.ledger?.category}
                                memo={m.ledger?.memo}
                                reference={m.ledger?.reference}
                                link={m.ledger?.link}
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              {m.combined?.length ? (
                                <CombinedSide lines={m.combined} />
                              ) : (
                                <Side
                                  amount={m.bank_feed?.amount}
                                  date={m.bank_feed?.date}
                                  category={m.bank_feed?.category}
                                  memo={m.bank_feed?.memo}
                                  reference={m.bank_feed?.reference}
                                  link={m.bank_feed?.link}
                                />
                              )}
                            </td>
                            <td className="px-3 py-2 align-top">
                              {row.match ? (
                                <span
                                  className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${conf.cls}`}
                                >
                                  {conf.label}
                                </span>
                              ) : (
                                // No evidence either side. Said plainly rather than
                                // left blank: an empty cell reads as "not loaded",
                                // and this is a finding — the money may have gone out
                                // without ever being booked.
                                <span className="inline-block rounded bg-palette-rose-soft px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-palette-rose-text">
                                  No match
                                </span>
                              )}
                              {/* The gaps, stated. A tick with no numbers behind it
                            asks for trust; these say what is being trusted. */}
                              {row.match ? (
                                <div className="mt-1 text-[10px] leading-tight text-palette-gray-secondary">
                                  by {m.matched_by ?? "amount"}
                                  <br />Δ ${m.amount_delta ?? "0.00"} · {m.day_delta ?? 0}d
                                  {/* WHY it is graded as it is. A grade with no
                                      reason attached is just a colour; this
                                      names the second, independent signal — and
                                      its absence is equally worth seeing. */}
                                  {m.corroborated_by && (
                                    <>
                                      <br />
                                      <span className="text-palette-emerald-text">
                                        {m.corroborated_by === "category"
                                          ? `both call it ${m.corroboration}`
                                          : `memo says “${m.corroboration}”`}
                                      </span>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <div className="mt-1 text-[10px] leading-tight text-palette-gray-placeholder">
                                  your call
                                </div>
                              )}
                            </td>
                            {/* A real button, not a decoration on a clickable
                                row: it gives the action a name, a focus stop and
                                a keyboard path, none of which a bare `onClick`
                                on the <tr> has. Always visible rather than shown
                                on hover — an affordance nobody can find until
                                they are already on top of it is not one. */}
                            {editable && (
                              <td className="px-3 py-2 align-top">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEditOccurrence(row);
                                  }}
                                  title="Edit this payment's date or amount, or skip it"
                                  className="inline-flex items-center gap-1 rounded-lg border border-palette-gray-divider bg-white px-2 py-1 text-[11px] font-medium text-palette-indigo-badge transition-colors hover:border-palette-indigo-ring hover:bg-palette-indigo-soft"
                                >
                                  <svg
                                    className="h-3 w-3"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <path
                                      d="M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                  Edit
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                  </tbody>
                );
              })}
            </table>
          </div>

          <p className="mt-3 text-xs text-palette-gray-placeholder">
            Confirming records your decision against each occurrence — it overrides the automatic
            matching from then on, and is not undone by a refresh. Nothing else is changed: dates
            and amounts stay as they are.
          </p>
        </div>
      }
    />
  );
}
